import type { TableMenuItem } from '@/components_deprecated'
import { SortableHeader, Table, TableMenu } from '@/components_deprecated'
import { useInfringementsMutations } from '@/modules/infringements/mutations.hooks'
import { IssueStatusCell } from '@/modules/issues/components/table/components'
import {
  useAdmin,
  useInfiniteScroll,
  useOnErrorAuto,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { DEFAULT_PAGE_SIZE } from '@/utils/pagination'
import { PERMISSIONS } from '@/utils/permissions'
import { NetworkStatus, useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { formatISO } from 'date-fns'
import React, { useCallback, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { useInfringementCategoryDescription } from '../../category-descriptions'
import { ALL_COMBINED_INFRINGEMENTS_QUERY } from '../../queries'
import {
  INFRINGEMENT_LEADER_PERMISSIONS,
  InfringementStatuses,
} from '../../consts'
import type {
  ArchivedInfringementForm,
  InfringementsTableFilter,
} from '../../types'
import type { InfringementNodeForAllInfringements } from '../../types.graphl'
import { ArchiveInfringementPrompt } from './ArchiveInfringementModal'
import { DateCell, EmployeeCell, SeverityCell } from './components'
import { InfringementsCards } from './InfringementsCards'
import { useCombinedInfringementVariables } from './use-combined-infringement-variables'
import type { CombinedRowView } from './combined-rows'
import { toCombinedRowView } from './combined-rows'
import { ActionLogTableRowAction } from '@/modules/issues/views/ActionLogTableRow'
import { useFollowUpActionText } from '../infringement-follow-up'
import { useFollowUpStateMutations } from '../use-follow-up-state'

interface DescriptionCellProps {
  hasData: boolean
}

const DescriptionCell = styled.td<DescriptionCellProps>`
  position: relative;
  max-width: 300px;
  ${props => props.hasData && 'cursor: auto;'};

  & > div {
    display: flex;
    flex-direction: column;
    overflow: hidden;

    font-size: 1rem;

    span {
      &.description {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        font-size: 0.9em;
        font-weight: 400;
        color: ${props => props.theme.colors.gray5};
      }
    }

    &.popup {
      display: none;
      min-width: 100%;
      max-width: 100%;
    }
  }

  &:hover > div.popup {
    position: absolute;
    top: -0.5rem;
    left: -0.5rem;
    z-index: 1000;

    display: flex;
    padding: 1.5rem;

    background: white;
    border-radius: ${props => props.theme.sizes.defaultBorderRadius};
    box-shadow: ${props => props.theme.shadow.default};

    span.description {
      white-space: normal;
      color: ${props => props.theme.colors.text.dark};
    }
  }
`

interface InfringementsTableProps {
  filter: InfringementsTableFilter

  onSort: (orderBy: string) => void
  detailPathBase?: string
}

export const InfringementsTable: React.FC<InfringementsTableProps> = ({
  filter,

  onSort,
  detailPathBase = '/infringements',
}) => {
  const categoryDescription = useInfringementCategoryDescription()
  const translations = useTranslate({
    date: 'common.date',
    employee: 'common.employee',
    archived: 'common.archived',
    open: 'common.open-alt',
    openInfringement: 'common.open',
    close: 'common.close',
    closed: 'common.closed',
    category: 'common.category',
    seeDetails: 'common.see-details',
    status: 'common.status',
    started: 'issues.started',
    action: 'common.action',

    noInfringements: 'infringements.no-infringements',
    noResults: 'common.no-results',

    severity: 'infringements.severity',
    archiveInfringement: 'infringements.archive-infringement',
    restoreInfringement: 'infringements.restore-infringement',
    wronglyGenerated: 'infringements.wrongly-generated',
    sources: {
      DRIVING_REST: 'infringements.source.driving-rest',
      WORKING_TIME: 'infringements.source.working-time',
      TACHOGRAPH_USE: 'infringements.source.tachograph-use',
    },
  })

  const navigate = useNavigate()
  const followUpActionText = useFollowUpActionText()
  const followUpState = useFollowUpStateMutations()

  const me = useUser()
  const admin = useAdmin()
  const { hasPermissions } = usePermissions()
  const onErrorAuto = useOnErrorAuto()
  const addPrompt = usePrompt()

  const filterVariables = useCombinedInfringementVariables(filter)
  const debouncedQuery = filterVariables.q

  const [infringementLoading, setInfringementLoading] = useState<string | null>(
    null
  )

  const canEdit = admin || hasPermissions(INFRINGEMENT_LEADER_PERMISSIONS)
  const canViewOthers = hasPermissions(
    PERMISSIONS.infringements.view.othersinfringement,
    true
  )

  const {
    data,
    loading: queryLoading,
    networkStatus,
    fetchMore,
  } = useQuery(ALL_COMBINED_INFRINGEMENTS_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      ...filterVariables,
      orderBy: filter.orderBy,
      first: DEFAULT_PAGE_SIZE,
    },
    onError: onErrorAuto(),
  })
  const cacheLoading: boolean =
    (queryLoading && !data) || networkStatus === NetworkStatus.refetch

  const page = data?.allCombinedInfringements
  const rows = useMemo(
    () =>
      (page?.rows ?? []).flatMap(row => {
        const view = toCombinedRowView(row, categoryDescription, detailPathBase)
        return view ? [{ view, infringement: row.infringement }] : []
      }),
    [page, categoryDescription, detailPathBase]
  )

  const {
    toggleInfringementArchived,
    loading: archiveInfringementLoading,
    toggleInfringementClosed,
  } = useInfringementsMutations()

  async function handleArchiveInfringement(
    infringement?: InfringementNodeForAllInfringements
  ) {
    if (typeof infringement === 'undefined') return

    if (infringement.archivedAt !== null) {
      void executeArchiveInfringement(infringement)()
      return
    }

    const initialData: ArchivedInfringementForm = {
      archivedBasis: '',
    }

    const { data } = await addPrompt<ArchivedInfringementForm | null>(
      resolve => (
        <ArchiveInfringementPrompt
          initialData={initialData}
          onResolve={resolve}
        />
      )
    )
    if (data === null) return

    void executeArchiveInfringement(infringement)(data)
  }

  function executeArchiveInfringement(
    infringement?: InfringementNodeForAllInfringements | null
  ) {
    if (!infringement) return () => void 0

    return async (data?: ArchivedInfringementForm) => {
      const isArchived = infringement.archivedAt !== null
      setInfringementLoading(infringement.id)

      await toggleInfringementArchived({
        variables: {
          id: infringement.id,
          input: {
            archivedBy: me.id,
            archivedAt: isArchived ? null : formatISO(new Date()),
            archivedBasis: data?.archivedBasis,
          },
        },
      })
      setInfringementLoading(null)
    }
  }

  async function handleCloseInfringement(
    infringement?: InfringementNodeForAllInfringements
  ) {
    if (typeof infringement === 'undefined') return

    void executeCloseInfringement(infringement)()
    return
  }

  function executeCloseInfringement(
    infringement?: InfringementNodeForAllInfringements | null
  ) {
    if (!infringement) return () => void 0

    return async () => {
      const isClosed =
        infringement.closedAt !== null || infringement.hasClosedIssue
      setInfringementLoading(infringement.id)

      await toggleInfringementClosed({
        variables: {
          id: infringement.id,
          input: {
            closedBy: me.id,
            closedAt: isClosed ? null : formatISO(new Date()),
          },
        },
      })
      setInfringementLoading(null)
    }
  }

  const loadedRows = page?.rows.length ?? 0
  const handleScrollBottom = useCallback(async () => {
    if (!page?.hasMore) return
    try {
      await fetchMore({
        variables: { offset: loadedRows },
        updateQuery: (previous, { fetchMoreResult }) => ({
          allCombinedInfringements: {
            ...fetchMoreResult.allCombinedInfringements,
            rows: [
              ...previous.allCombinedInfringements.rows,
              ...fetchMoreResult.allCombinedInfringements.rows,
            ],
          },
        }),
      })
    } catch (e) {
      if (e instanceof Error && e.name === 'Invariant Violation') return
      throw e
    }
  }, [fetchMore, loadedRows, page?.hasMore])

  useInfiniteScroll(handleScrollBottom, 200, !queryLoading && page?.hasMore)

  async function toggleViewArchived(view: CombinedRowView) {
    if (view.source === 'DRIVING_REST') return
    const archived = view.status === InfringementStatuses.ARCHIVED
    if (archived) {
      await followUpState.setArchived(view.source, view.id, false)
      return
    }
    const { data } = await addPrompt<ArchivedInfringementForm | null>(
      resolve => <ArchiveInfringementPrompt onResolve={resolve} />
    )
    if (!data) return
    await followUpState.setArchived(
      view.source,
      view.id,
      true,
      data.archivedBasis
    )
  }

  function createViewMenuItems(view: CombinedRowView) {
    const items: TableMenuItem<CombinedRowView>[] = [
      {
        label: translations.seeDetails,
        data: view,
        role: 'link',
        onClick: () => void navigate(`${view.detailPathBase}/${view.id}`),
      },
    ]
    if (canEdit && view.source !== 'DRIVING_REST') {
      const source = view.source
      const isClosed = view.status === InfringementStatuses.CLOSED
      items.push({
        label:
          view.status === InfringementStatuses.ARCHIVED
            ? translations.restoreInfringement
            : translations.archiveInfringement,
        data: view,
        role: 'link',
        onClick: () => void toggleViewArchived(view),
      })
      items.push({
        label: isClosed ? translations.openInfringement : translations.close,
        data: view,
        role: 'link',
        onClick: () => void followUpState.setClosed(source, view.id, !isClosed),
      })
    }
    return items
  }

  function createMenuItems(infringement: InfringementNodeForAllInfringements) {
    const items: TableMenuItem<InfringementNodeForAllInfringements>[] = [
      {
        label: translations.seeDetails,
        data: infringement,
        role: 'link',
        onClick: () => void navigate(`${detailPathBase}/${infringement.id}`),
      },
    ]

    if (canEdit) {
      const isArchived = infringement.archivedAt !== null
      const isClosed =
        infringement.closedAt !== null || infringement.hasClosedIssue
      items.push({
        label: isArchived
          ? translations.restoreInfringement
          : translations.archiveInfringement,
        data: infringement,
        role: 'link',
        onClick: handleArchiveInfringement,
      })
      items.push({
        label: isClosed ? translations.openInfringement : translations.close,
        data: infringement,
        role: 'link',
        onClick: handleCloseInfringement,
      })
    }
    return items
  }

  const filterActive = debouncedQuery !== ''
  const noDataText = filterActive
    ? translations.noResults
    : translations.noInfringements

  return (
    <>
      {isMobile ? (
        <InfringementsCards
          rows={rows.map(row => row.view)}
          queryLoading={queryLoading}
          showUser={canViewOthers}
          renderMenu={view => {
            const drivingRest = rows.find(row => row.view.key === view.key)
              ?.infringement
            return drivingRest ? (
              <TableMenu
                items={createMenuItems(drivingRest)}
                loading={infringementLoading === drivingRest.id}
                loaderSize={18}
                loaderThickness={2}
              />
            ) : (
              <TableMenu
                items={createViewMenuItems(view)}
                loading={followUpState.loading}
                loaderSize={18}
                loaderThickness={2}
              />
            )
          }}
          onLoadMore={handleScrollBottom}
        />
      ) : (
        <Table
          loading={cacheLoading || archiveInfringementLoading}
          loaderProps={{
            columns: 3,
            rows: 10,
            expectedWidths: [92, 82, 162],
          }}
          noData={rows.length === 0}
          noDataText={noDataText}
        >
          <thead>
            <tr>
              <SortableHeader
                baseValue="infringementTimestamp"
                sortValue={filter.orderBy}
                invertDirection
                onSort={onSort}
              >
                {translations.date}
              </SortableHeader>
              <SortableHeader
                baseValue="user__firstName"
                sortValue={filter.orderBy}
                disabled={!admin}
                onSort={onSort}
              >
                {translations.employee}
              </SortableHeader>

              <th>{translations.category}</th>

              <SortableHeader
                baseValue="infringementCategory__severity"
                sortValue={filter.orderBy}
                onSort={onSort}
              >
                {translations.severity}
              </SortableHeader>
              <th>{translations.status}</th>
              <th>{translations.action}</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {rows.map(({ view, infringement }) => {
              const hasData = !!view.text
              const drivingRest = infringement ?? null

              return (
                <tr key={view.key}>
                  <DateCell
                    infringementId={view.id}
                    createdAt={view.timestamp}
                    detailPathBase={view.detailPathBase}
                  />

                  <EmployeeCell user={view.user} />

                  <DescriptionCell hasData={hasData}>
                    <div className="flat">
                      <span className="description">
                        <b>{view.identifier}.</b> {view.text}
                      </span>
                      {view.source !== 'DRIVING_REST' && (
                        <span className="text-xs text-muted-foreground">
                          {translations.sources[view.source]}
                          {view.wronglyGenerated &&
                            ` · ${translations.wronglyGenerated}`}
                        </span>
                      )}
                    </div>
                    {hasData && (
                      <div className="popup">
                        <span className="description">
                          <b>{view.identifier}.</b> {view.text}
                        </span>
                      </div>
                    )}
                  </DescriptionCell>

                  {view.severity ? (
                    <SeverityCell severity={view.severity} />
                  ) : (
                    <td>-</td>
                  )}
                  <IssueStatusCell
                    closed={view.status === InfringementStatuses.CLOSED}
                    archived={view.status === InfringementStatuses.ARCHIVED}
                    edited={view.status === InfringementStatuses.STARTED}
                    editText={translations.started}
                    openText={translations.open}
                    archivedText={translations.archived}
                    closedText={translations.closed}
                  />

                  {drivingRest?.latestActionLog ? (
                    <ActionLogTableRowAction
                      node={{
                        id: drivingRest.latestActionLog.id,
                        action: drivingRest.latestActionLog.action,
                        createdAt: drivingRest.latestActionLog.createdAt,
                        user: drivingRest.latestActionLog.user ?? undefined,
                        secondaryUser:
                          drivingRest.latestActionLog.secondaryUser ??
                          undefined,
                      }}
                    />
                  ) : view.latestActionLog ? (
                    <td>{followUpActionText(view.latestActionLog)}</td>
                  ) : (
                    <DescriptionCell hasData={false}></DescriptionCell>
                  )}

                  {drivingRest ? (
                    <TableMenu
                      items={createMenuItems(drivingRest)}
                      loading={queryLoading}
                    />
                  ) : (
                    <TableMenu
                      items={createViewMenuItems(view)}
                      loading={queryLoading}
                    />
                  )}
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}
    </>
  )
}
